import { Application, Container, Graphics, Sprite, type Texture, type Ticker } from 'pixi.js';
import {
  SIM,
  SOCIAL,
  WORLD_THEMES,
  SEAT_INTERACT_RANGE,
  OBJECT_INTERACT_RANGE,
  DOOR_REACH,
  clamp,
  dayPhase,
  mayOpenHeldDoor,
  resolveWallCollisionIn,
  worldTheme,
  zoneAtIn,
  zonesConnectedOpenIn,
  entryPredicates,
  LEVELS,
  levelForTheme,
  HEALTH,
  NEIGHBOR_PRESENCE_ALPHA,
  type DoorOpenState,
  type Level,
  type LevelId,
  type WorldWall,
  type Doorway,
  type DayPhase,
  type DuckColor,
  type DuckSpecies,
  type InteractiveObject,
  type PresenceFlavor,
  type Seat,
  type Weather,
  type WorldEventKind,
  type WorldThemeId,
} from '@duck/contracts';
import { Critter } from './critter';
import { createOfficeMap, layoutForLevel } from './office-map';
import { AmbientLayer } from './ambient';
import { WorldEventLayer } from './world-event';
import { DiscoZoneLayer } from './disco-zone';
import { FurnitureLayer } from './furniture-layer';
import { buildFurnitureGhost, buildGhostFromDecor, setGhostValid, snap, PLACEMENT_GRID } from './placement-controller';
import { themeArt, type ThemeArt } from './theme-art';
import { InteractHighlight } from './interact-highlight';
import { CelebrationBurst, DuckBurst } from './celebration';
import { FURNITURE_CATALOG, furnitureObjectKind, isSittableFurniture } from '@duck/contracts';
import type { FurnitureItem, PlacedFurnitureView } from '@duck/contracts';

/** decorKind → display name (for the context-menu title), from the buyable catalog. */
const FURNITURE_NAME_BY_KIND = new Map<string, string>(FURNITURE_CATALOG.map((f) => [f.decorKind, f.name]));

/**
 * Whether the LIVE office surfaces the STATIC hardcoded interactables (the office's built-in
 * `OFFICE_OBJECTS` — coffee/workstation/sofa/tv/arcade/jukebox — and `OFFICE_SEATS` meeting/desk anchors).
 * The default room now ships BARE (no hardcoded props), so these are not drawn; this flag also hides their
 * "Press E" prompts / highlights / interactions so a player never gets a phantom prompt over empty floor.
 * The contracts data + the server's INTERACT/SIT + the meeting/WORK quest systems stay intact (so flipping
 * this back to `true` restores them wholesale). Furniture-Store pieces are unaffected — they're a separate
 * path (context-menu Interact/Sit), so a placed coffee machine / chair still works.
 */
export const STATIC_INTERACTABLES: boolean = false;

/**
 * Which levels ship BARE (dual-level coexistence, ADR-0054): the OFFICE is bare — every prop is an editable
 * Furniture-Store piece, so the static backdrop + interactive objects/seats are dropped from the bake, the
 * "Press E" prompts over them are suppressed, and the ambient layer gets NO objects (no idle glows/steam over
 * unbaked props). The CASTLE is NOT bare — it bakes its rich per-room decorators + objects. This per-level
 * rule supersedes the old global {@link STATIC_INTERACTABLES} inside the engine (kept for any external use).
 */
export function bareLevelFor(levelId: LevelId): boolean {
  return levelId === 'office';
}

/**
 * A right-click / long-press CONTEXT-MENU request the engine raises for the React layer to render. The
 * target is either a placed FURNITURE piece (move/rotate/remove — `canModify` gates them to the placer or
 * host) or an interactive OBJECT (the same "Взаимодействовать"/E action). `screenX/Y` are CSS px for menu
 * placement. Cosmetic furniture is never `interactive`; only the static interactive objects are.
 */
export interface ContextMenuRequest {
  screenX: number;
  screenY: number;
  target:
    | {
        type: 'furniture';
        id: string;
        kind: string;
        name: string;
        canModify: boolean;
        /** This placed piece is an interactive prop (coffee/jukebox/tv/arcade) → offer "Interact". */
        interactive: boolean;
        /** A duck can sit on this piece → offer "Sit here" / "Stand up". */
        sittable: boolean;
        /** The LOCAL duck is currently seated on this piece → the sit action reads "Stand up". */
        seatedHere: boolean;
        /** ANOTHER duck is already seated on this piece → the sit action is disabled ("Taken"). */
        occupied: boolean;
      }
    | { type: 'object'; objectId: string; label: string; icon: string };
}
import { officeRenderResolution, resolveRenderTier, type DeviceTier } from '../../../lib/device-tier';
import { useRenderSettingsStore } from '../../../state/render-settings.store';
import { KeyboardInput } from '../../../net/input';
import { type RealtimeClient } from '../../../net/realtime-client';
import { sampleBuffer, trimBuffer, type Snapshot } from '../../../net/interpolation';

const SEND_INTERVAL_MS = 1000 / SIM.TICK_RATE;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.6;

/** Equipped-look shape for the dev `__duckWorld()` snapshot (M6): empty slots are omitted. */
type DebugCosmetics = { hat?: string; glasses?: string; outfit?: string; pet?: string };

/**
 * Map a synced cosmetics sub-schema (where '' = empty slot) to the e2e snapshot shape, omitting empty
 * slots so an assertion reads the broadcast look exactly (e.g. `{ hat: 'hat-tophat' }`).
 */
function toDebugCosmetics(
  c: { hat?: string; glasses?: string; outfit?: string; pet?: string } | undefined,
): DebugCosmetics {
  const out: DebugCosmetics = {};
  if (c?.hat) out.hat = c.hat;
  if (c?.glasses) out.glasses = c.glasses;
  if (c?.outfit) out.outfit = c.outfit;
  if (c?.pet) out.pet = c.pet;
  return out;
}
/**
 * Max distance to a teammate you can give reputation to. Shared with the server (which re-checks it
 * authoritatively) so the client only ever targets ducks the server will actually accept.
 */
const GIVE_REPUTATION_RANGE = SOCIAL.REPUTATION_GIVE_RANGE;

/**
 * Max distance to a duck you can challenge to Rock-Paper-Scissors (M7b-2). Shared with the server
 * (which re-checks it authoritatively) so the client only ever targets ducks the server will accept.
 */
const MINIGAME_RANGE = SOCIAL.MINIGAME_RANGE;

/** An entryway front-door action the local duck can take right now (ADR-0022). */
export type EntryActionKind = 'ring' | 'knock' | 'open';
/** The nearest entry action + its dual-coded prompt copy (drives the HUD "Press E" front-door prompt). */
export interface EntryActionView {
  kind: EntryActionKind;
  icon: string;
  label: string;
}

/**
 * The interior-door action the local duck can take right now (Room Doors feature): the nearest doorway in
 * reach (or, for the HOST, any door they target via the panel) + whether the act would OPEN it (it's
 * currently closed) or CLOSE it. Drives the dual-coded "[E] Open/Close door" prompt.
 */
export interface DoorActionView {
  doorwayId: string;
  /** True when the door is currently CLOSED → the action OPENS it; false → the action CLOSES it. */
  open: boolean;
  /** True when the door is held shut by someone else (ADR-0060) — the action becomes a polite KNOCK. */
  locked: boolean;
  icon: string;
  label: string;
}

/** One rendered interior-door leaf + its eased open amount (1 = open/invisible, 0 = closed/filling the gap). */
interface DoorLeaf {
  container: Container;
  axis: 'h' | 'v';
  amount: number;
}

/**
 * Owns the Pixi application and the per-frame loop: local prediction for the player's own duck,
 * entity interpolation for everyone else, a follow camera, and throttled intent sending. It reads
 * authoritative state from {@link RealtimeClient} (never mutates it) and is fully torn down on
 * {@link destroy}. Kept outside React so 60 FPS rendering never triggers re-renders.
 */
export class OfficeEngine {
  private app: Application | null = null;
  private readonly world = new Container();
  private readonly actors = new Container();
  private readonly fxLayer = new Container();
  /**
   * Procedural ambient atmosphere (M4.2): day/night lighting, weather, and tiny ambient life. Lives
   * BELOW the ducks (added between the map and the actors) so its tint colours the WORLD/backdrop only
   * and never dims the foreground — ducks, labels, badges and the DOM HUD stay legible (a11y gate).
   */
  // Built once the level is resolved (Phase 3) so its glows/steam + world-size overlays match the world.
  private ambient: AmbientLayer = new AmbientLayer([], LEVELS.office.worldWidth, LEVELS.office.worldHeight, LEVELS.office.fireHazards); // office is bare → no idle glows/steam over unbaked props
  /**
   * Procedural rendering of the SHARED random world events (M7b): pizza / fire_drill. Like the ambient
   * sky, the active event lives on the authoritative room state, so the whole office sees the same
   * spontaneous moment. Lives BELOW the ducks (added between the ambient layer and the actors) so its
   * props sit on the FLOOR, never occluding/dimming the foreground (a11y). (Disco moved to {@link disco}
   * — it's now the room-local jukebox toggle, ADR-0031.)
   */
  // Built once the level is resolved (Phase 3) so shared events anchor in the resolved world's rooms.
  private worldEvent: WorldEventLayer = new WorldEventLayer(LEVELS.office.zones, LEVELS.office.worldWidth, LEVELS.office.worldHeight);
  /** Last shared event we applied to {@link worldEvent}, so we only rebuild on a real change. */
  private worldEventKind: WorldEventKind = 'none';
  /**
   * Per-room disco (ADR-0031 "Jukebox") — lights ONLY the rooms in the authoritative `discoZones` set
   * (toggled by the jukebox), never the whole floor. Reconciled on a real change to that set.
   */
  private readonly disco = new DiscoZoneLayer();
  /** Last lit-zones signature we applied to {@link disco}, so we only reconcile on a real change. */
  private discoZonesKey = '';
  /**
   * Placed FURNITURE (Furniture Store) — renders the room's shared, persisted furniture from the synced
   * `furniture` state. Lives BELOW the ducks (added alongside the highlight layer). Reconciled on a real
   * change to the placed-piece set (furniture is immutable once placed, so the id set is the signature).
   */
  private readonly furnitureLayer = new FurnitureLayer();
  /** Last placed-furniture id signature we reconciled, so we rebuild only when a piece is added/removed. */
  private furnitureSig = '';
  /** The ThemeArt for the current world theme (built on init + theme swap) — reused to build furniture/ghost. */
  private currentArt: ThemeArt | null = null;
  /**
   * The currently ARMED placement, or null. Two modes share the ghost-follow-cursor machinery:
   *  - 'buy'  — placing a freshly-bought catalog item (Store "Buy & Place"); confirm → server BUY+place.
   *  - 'move' — carrying an already-placed piece ("Take" from the context menu); confirm → server MOVE
   *             (no coins). The carried piece is hidden in place while it follows the cursor.
   */
  private armed:
    | { mode: 'buy'; item: FurnitureItem }
    | { mode: 'move'; furnitureId: string; kind: string; w?: number; h?: number; rotation: number }
    | null = null;
  /** The translucent placement ghost following the cursor while {@link armed} is set. */
  private furnitureGhost: Container | null = null;
  /**
   * In MOVE mode ("Take"), a small solid copy of the carried piece held ABOVE the local duck's head — so
   * the character visibly walks around with it in hand (ТЗ). Mounted on the self-duck's `root` (so it
   * follows movement for free), removed on drop/cancel. The cursor ghost still shows where it will land.
   */
  private carriedOverlay: Container | null = null;
  /**
   * The themed office map (M8 "Guilds at Scale") — the SAME geometry, re-skinned to the guild's world
   * theme. Kept as a reference so {@link applyTheme} can swap it (rebuild on a theme change only, never
   * per frame) when an owner switches the guild's theme. Lives at the bottom of {@link world}, below the
   * ambient/event washes and the ducks, so re-skinning never touches the foreground.
   */
  private map: Container | null = null;
  /**
   * The GPU texture backing the BAKED static map (ADR-0026). The office map is ~thousands of static
   * vertices that Pixi re-processes every frame as the camera moves — the single largest client cost in
   * a populated room (hiding the map ~doubled FPS at N=40). We render it ONCE to this texture and draw
   * the whole backdrop as one Sprite, re-baking only when the guild theme changes. Owned here so it can
   * be explicitly destroyed on re-bake / teardown (it is not pooled).
   */
  private mapTexture: Texture | null = null;
  /**
   * The guild's WORLD THEME currently rendered (M8). The whole point of M8: the engine is the product,
   * not the office — the same world re-skins per guild (Office HQ / Orbital Station / Wizard Academy /
   * Cozy Café). Read off the authoritative room state every frame (like {@link worldEvent}); we only
   * re-skin on a real change. Defaults to 'office' so an older room / pre-first-patch reads as the HQ.
   */
  private themeId: WorldThemeId = 'office';
  /**
   * The resolved GEOMETRY level the engine renders + collides + fades against (Phase 3 two-level refactor).
   * Authoritative source = the synced `room.state.levelId` ('office' | 'medieval_castle'); falls back to the
   * cosmetic theme if the field is absent (older room / pre-first-patch). The LEVEL is geometry only — the
   * cosmetic {@link themeId} still drives the skin via {@link applyTheme}, so the two stay decoupled (a
   * castle could in principle be skinned by any theme; today they move together). Resolved once when state
   * is available and rebuilt defensively if `levelId` changes (mirrors {@link applyTheme}).
   */
  private level: Level = LEVELS.office;
  /** The level id last resolved into {@link level}, so we only rebuild the world when it actually changes. */
  private levelId: LevelId = 'office';
  /** The entry-ritual predicates bound to {@link level}'s zones + entry config (rebuilt on a level change). */
  private entry = entryPredicates(this.level.zones, this.level.entry);
  private readonly input = new KeyboardInput();

  /**
   * The local player's coin balance (M6), pushed by the React layer from the gateway-attested
   * progression/inventory (coins are NOT on the realtime schema). Used only to surface `coins` in the
   * dev `__duckWorld()` snapshot for the e2e — never used by render/product logic.
   */
  selfCoins: number | null = null;

  /** Set by the React layer: called when the player gives reputation to a nearby teammate. */
  onGiveReputation?: (targetUserId: string, targetSessionId: string) => void;
  /**
   * Set by the React layer: called when the player TAPS a peer's webcam bubble in the world (ADR-0030),
   * to pin that camera into the HUD. Per-viewer only — pinning is a private UI choice that never touches
   * the server or the pinned person (no "you are being watched" signal exists).
   */
  onWebcamClick?: (targetSessionId: string) => void;
  /**
   * sessionId → that player's live webcam stream (SELF — your own outgoing camera — and each nearby peer
   * whose face the server forwards to you). Pushed from the voice client via {@link setPeerWebcam}; the
   * render loop attaches each to the matching duck's head bubble, respecting the same off-screen cull as
   * the avatar itself (a face you can't see the duck of never renders).
   */
  private readonly webcamStreams = new Map<string, MediaStream>();
  /**
   * Set by the React layer: called when the player challenges the nearest other duck to a minigame
   * (M7b-2 "Play Together" — `game` picks RPS or the Bark Battle). Picks the target with the SAME
   * proximity logic give-reputation uses (the engine owns the predicted self position; the realtime
   * re-checks proximity authoritatively).
   */
  onChallengeNearest?: (targetUserId: string, targetSessionId: string, game: 'rps' | 'bark') => void;
  /** Set by the React layer: called when the player uses (interacts with) a nearby world object (M4). */
  onInteract?: (objectId: string) => void;
  /**
   * Set by the React layer: called when the player CONFIRMS a furniture placement (Furniture Store) by
   * clicking a spot while a piece is armed. Carries the catalog `itemId` + the snapped world position; the
   * React layer routes it to the realtime client (which asks the gateway to charge coins + persist). Coins
   * are charged ONLY here on confirm — cancelling (Esc/right-click) never calls this, so nothing is spent.
   */
  onPlaceFurniture?: (itemId: string, x: number, y: number) => void;
  /**
   * Set by the React layer: raise (or clear, with null) a CONTEXT-MENU request — fired on right-click /
   * long-press over a placed furniture piece or an interactive object. React renders the menu at
   * `screenX/Y` and routes its actions back to the engine ({@link startMoveFurniture}/{@link rotateFurniture})
   * / client (remove) / object interact. Cleared (null) when the menu should close.
   */
  onContextMenu?: (request: ContextMenuRequest | null) => void;
  /**
   * Set by the React layer: emitted (on change) with the nearest interactable object to the local
   * duck, or null when out of range — drives the HUD's dual-coded "Press E" prompt/affordance (M4).
   */
  onNearestObjectChange?: (object: InteractiveObject | null) => void;
  /** Last value pushed to {@link onNearestObjectChange}, so we only emit on a real change. */
  private nearestObjectId: string | null = null;

  /**
   * Whether the local player is the session HOST (the workspace OWNER), pushed by the React layer. The
   * host may OPEN the entry door from anywhere (even the vestibule), so this decides whether a duck in
   * the entryway sees "Open the door" (host) or "Knock" (guest). The SERVER re-checks it regardless — this
   * is purely which prompt to show (ADR-0022).
   */
  isHost = false;
  /**
   * Set by the React layer: emitted (on change) with the nearest ENTRYWAY action available to the local
   * duck — ring the bell, knock, or open the door — or null when none is in range. Drives the HUD's
   * dual-coded "Press E" front-door prompt (ADR-0022).
   */
  onNearestEntryActionChange?: (action: EntryActionView | null) => void;
  /** Set by the React layer: perform an entry action (ring / knock / open) — routes to the realtime client. */
  onEntryAction?: (kind: EntryActionKind) => void;
  /** Last entry action pushed to {@link onNearestEntryActionChange}, so we only emit on a real change. */
  private nearestEntryAction: EntryActionView | null = null;
  /** The dynamic entry-door panel (the leaf that fills the doorway when closed) — toggled each frame. */
  private doorPanel: Container | null = null;
  /** Eased 0..1 "openness" of the entry door (0 closed, 1 open) so the leaf swings smoothly, not pops. */
  private doorOpenAmount = 0;
  /**
   * Interior room-door leaves (Room Doors), keyed by {@link Doorway} id. Each swings toward its synced
   * open/closed state every frame (like the entry door), so a shut door reads as a real leaf filling the
   * gap and an open one swings out of the way. Built once with the world; lives in {@link doorLeafLayer}.
   */
  private readonly interiorDoors = new Map<string, DoorLeaf>();
  private readonly doorLeafLayer = new Container();
  /**
   * Set by the React layer: emitted (on change) with the nearest interior-door action available to the
   * local duck (open/close the doorway in reach), or null. Drives the HUD's "[E] Open/Close door" prompt.
   */
  onNearestDoorActionChange?: (action: DoorActionView | null) => void;
  /** Set by the React layer: perform an interior-door toggle — routes to the realtime client. */
  onDoorAction?: (doorwayId: string, open: boolean, locked?: boolean) => void;
  /** Last door action pushed to {@link onNearestDoorActionChange}, so we only emit on a real change. */
  private nearestDoorAction: DoorActionView | null = null;
  /**
   * Set by the React layer: emitted (on change) with the WHOLE door state — the ids of the closed interior
   * doors, whether the entry door is open, and which closed doors are HELD by a present citizen (ADR-0060,
   * the panel's 🔒 badge) — so the host's "Doors" panel reflects live state (including doors others
   * toggle). Read off authoritative room state each frame; emitted only on a real change.
   */
  onDoorsChange?: (closedDoorIds: string[], entryDoorOpen: boolean, heldDoorIds: string[]) => void;
  /** Signature of the last door state pushed to {@link onDoorsChange}, so we only emit on a real change. */
  private lastDoorsSig = '';

  /**
   * Interact HIGHLIGHTS (UX "objects light up when usable"): a layer above the floor/washes, below the
   * ducks, holding two soft pulsing rings — one for the nearest in-range OBJECT, one for the nearest free
   * in-range SEAT — so the player SEES what "E" will act on. Suppressed while seated (E just stands you
   * up). Purely a visual affordance, paired with the DOM "Press E" prompt (never a canvas-only cue).
   */
  private readonly highlightLayer = new Container();
  private readonly objectHighlight = new InteractHighlight();
  private readonly seatHighlight = new InteractHighlight();

  private readonly ducks = new Map<string, Critter>();
  /** Session ids the server marks as currently speaking (ADR-0009). Reapplied as ducks (re)spawn. */
  private readonly speakingSessionIds = new Set<string>();
  /** Live LOUDNESS (0..1) per speaking session (voice-client meter). Swells a louder voice's duck. */
  private speakingLevels = new Map<string, number>();
  private readonly buffers = new Map<string, Snapshot[]>();
  private readonly lastSample = new Map<string, { x: number; y: number }>();
  private readonly bursts: (CelebrationBurst | DuckBurst)[] = [];

  // Seeded inside the entry vestibule (Floorplan v2 / ADR-0022), matching `spawnPosition`; reseeded from
  // our authoritative spawn the moment it arrives.
  private predicted = { x: SIM.WORLD_WIDTH / 2, y: SIM.WORLD_HEIGHT - 120, facing: -Math.PI / 2 };
  private camera = { x: this.predicted.x, y: this.predicted.y, zoom: 1 };
  // Stable bound viewport predicate (created once → no per-frame closure alloc) handed to the ambient
  // layer so it can cull off-screen life — same on-screen test (+140px margin) the avatar cull uses.
  private readonly cullTest = (wx: number, wy: number): boolean => this.isOnScreen(wx, wy);
  private seq = 0;
  private sendAccum = 0;
  /** Was the last MOVE we actually sent a moving (non-zero) intent? Gates the idle-MOVE drop (cost-census). */
  private lastSentMoving = false;
  private destroyed = false;

  constructor(
    private readonly container: HTMLElement,
    private readonly client: RealtimeClient,
  ) {}

  async init(): Promise<void> {
    const app = new Application();
    await app.init({
      resizeTo: this.container,
      antialias: true,
      background: 0x0b1020,
      // perf cost-census: clamp the office pixel ratio by device tier (≤2× strong, ≤1.5× weak). Fill-rate
      // scales with the square of this, so it is the dominant GPU lever on low-power hardware — and the
      // picker already renders the same critters at ≤2×, so strong machines see no visible change. The
      // player's saved preference (auto/full/lite) overrides the auto-detect; applyRenderTier swaps it live.
      resolution: officeRenderResolution(undefined, resolveRenderTier(useRenderSettingsStore.getState().tier)),
      autoDensity: true,
    });
    if (this.destroyed) {
      app.destroy(true);
      return;
    }
    this.app = app;
    this.container.appendChild(app.canvas);

    // M8 "Guilds at Scale": skin the world to the guild's theme on the way in. Read the SAME
    // authoritative theme off the room state the client passed on join, build the themed map, and tint
    // the sky/backdrop to it — the layout/mechanics are identical under every theme, only the look shifts.
    this.themeId = this.client.theme();
    // Phase 3: resolve the authoritative GEOMETRY level from the synced `levelId` BEFORE building the map,
    // so the backdrop + collision + entry predicates all target the same world the server runs (office or
    // castle). Geometry is decoupled from the cosmetic theme below.
    this.levelId = this.client.levelId();
    this.level = LEVELS[this.levelId] ?? levelForTheme(this.themeId);
    this.entry = entryPredicates(this.level.zones, this.level.entry);
    const theme = worldTheme(this.themeId);
    this.currentArt = themeArt(theme); // reused to build placed furniture + the placement ghost
    // ADR-0026: render the static backdrop to one sprite — built from the resolved level's layout + size.
    // bareLevel (office only): the default OFFICE ships EMPTY — drop ALL hardcoded clutter (decorator
    // furniture + the untagged painted backdrop + the interactive OBJECTS); every visible piece is now
    // editable furniture the host places (Furniture Store). The CASTLE is NOT bare — its rich per-room
    // decorators (hearth/forge/throne/…) + objects bake as before. Per-level via {@link bareLevelFor}.
    this.map = this.bakeMap(
      createOfficeMap(theme, layoutForLevel(this.level), this.level.worldWidth, this.level.worldHeight, {
        bareLevel: bareLevelFor(this.levelId),
        levelId: this.levelId,
      }),
    );
    this.world.addChild(this.map);
    // Entryway front door (ADR-0022): a dynamic leaf that fills the doorway when closed and swings open
    // when the synced `entryDoorOpen` flips. Above the map (so it sits in the wall opening), below the
    // ambient wash + ducks (so night tints it like the rest of the building and ducks draw in front).
    this.doorPanel = this.buildDoorPanel();
    this.world.addChild(this.doorPanel);
    // Interior room doors (Room Doors): one leaf per controllable doorway, swinging toward its synced
    // open/closed state. Same layer band as the entry leaf (in the wall opening, below ambient + ducks).
    this.buildInteriorDoors();
    this.world.addChild(this.doorLeafLayer);
    // Build the ambient layer for the RESOLVED level (Phase 3): its objects seed the glows/steam and its
    // world size drives the lighting/cloud overlays + rain envelope (castle ≫ office). Replaces the office
    // default created at field-init (which was never added to the world).
    this.ambient = new AmbientLayer(bareLevelFor(this.levelId) ? [] : this.level.objects, this.level.worldWidth, this.level.worldHeight, this.level.fireHazards);
    this.world.addChild(this.ambient.root); // ambient sky/weather tints the backdrop, below the ducks
    // Build the shared-event layer for the resolved level so events anchor in its rooms (Phase 3).
    this.worldEvent = new WorldEventLayer(this.level.zones, this.level.worldWidth, this.level.worldHeight);
    this.world.addChild(this.worldEvent.root); // shared world events (pizza/fire) sit on the floor
    this.disco.setZones(this.level.zones); // clip the jukebox disco wash to the resolved level's rooms
    this.world.addChild(this.disco.root); // per-room jukebox disco wash (ADR-0031), also on the floor
    // Interact highlights sit ABOVE the floor + washes (so they're never dimmed) but BELOW the ducks
    // (so a duck standing on the pad still draws over it). Survives a theme re-skin (the map swaps at
    // index 0; this layer is a separate child the swap never touches).
    this.highlightLayer.addChild(this.objectHighlight.root, this.seatHighlight.root);
    this.world.addChild(this.highlightLayer);
    // Placed furniture (Furniture Store) sits on the floor ABOVE the washes but BELOW the ducks (so a duck
    // standing on a rug draws over it), same band as the highlight layer. Reconciled from synced state.
    this.world.addChild(this.furnitureLayer.root);
    this.actors.sortableChildren = true;
    this.world.addChild(this.actors);
    this.world.addChild(this.fxLayer); // celebrations draw above everyone
    app.stage.addChild(this.world);
    // Compose the theme sky into the day/night wash, and tint the canvas clear-colour to it so the
    // letterboxing around the world reads as the same place (e.g. the station's deep void).
    this.ambient.setSkyTint(theme.palette.sky);
    app.renderer.background.color = this.themeBackground(theme.palette.sky);

    // Seed prediction from our authoritative spawn, if already present.
    const self = this.client.self();
    if (self) {
      this.predicted = { x: self.x, y: self.y, facing: self.facing };
      this.camera = { ...this.camera, x: self.x, y: self.y };
    }

    this.input.attach();
    app.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    // Right-click / touch long-press → the furniture+object context menu (ТЗ). Persistent (unlike the
    // armed-placement listeners): when nothing is armed it opens the menu; while armed it cancels.
    app.canvas.addEventListener('contextmenu', this.onCanvasContextMenu);
    app.canvas.addEventListener('pointerdown', this.onCanvasPointerDownLong);
    app.canvas.addEventListener('pointermove', this.onCanvasPointerMoveLong);
    app.canvas.addEventListener('pointerup', this.onCanvasPointerUpLong);
    app.canvas.addEventListener('pointercancel', this.onCanvasPointerUpLong);
    app.ticker.add(this.loop);
    // perf cost-census: cap the render loop at 60 FPS. On a 120Hz display the office held a locked 120 FPS,
    // but rendering >60 gives no perceptible benefit while doubling per-frame CPU/GPU — wasteful exactly on
    // the weak laptops we target. dt-based animation stays correct (larger deltaMS/frame); the 20Hz MOVE/sim
    // cadence is unaffected (it accumulates real elapsed time, not frames).
    app.ticker.maxFPS = 60;

    // Dev-only observability for the e2e "manual testing agent" (never used by product logic) — mirrors
    // `window.__duckVoice`. Reports our own elected presence + activity and the nearest interactable.
    if (typeof window !== 'undefined' && import.meta.env?.DEV) {
      (window as unknown as { __duckWorld?: () => unknown }).__duckWorld = () => this.debugSnapshot();
      // M7a "Socialize": the most recent received speech line / reaction (SELF and OTHERS) so the e2e can
      // assert a SAID/REACTED broadcast landed even though the bubble/emoji are canvas-drawn (AT-invisible).
      (window as unknown as { __duckSocial?: () => unknown }).__duckSocial = () => ({
        lastSaid: this.client.lastSaid,
        lastReacted: this.client.lastReacted,
      });
      // Furniture Store (ТЗ): read the synced placed-furniture set + drive a buy-&-place deterministically
      // for the e2e. `place` routes through the SAME confirmed-placement path the click uses (→ server
      // economy), so the harness exercises real price/balance/cap authority — not a client-side shortcut.
      (window as unknown as { __duckFurniture?: () => unknown }).__duckFurniture = () => ({
        placed: this.client.furniture(),
        count: this.client.furniture().length,
        coins: this.selfCoins,
        place: (itemId: string, x: number, y: number) => this.onPlaceFurniture?.(itemId, x, y),
        arm: (item: FurnitureItem) => this.armFurniture(item),
        cancel: () => this.cancelFurniture(),
        // Context-menu actions, driven directly for the e2e (the menu UI is React/DOM; these are the
        // same handlers it calls). `move`/`rotate`/`remove` route to the server via the realtime client.
        move: (id: string, x: number, y: number, rotation?: number) => this.client.moveFurniture(id, x, y, rotation),
        rotate: (id: string) => this.rotateFurniture(id),
        remove: (id: string) => this.client.removeFurniture(id),
        take: (id: string) => this.startMoveFurniture(id),
        carrying: () => this.carriedOverlay !== null,
        interact: (id: string) => this.client.interact(id),
        sit: (id: string) => this.client.sit(id),
        pickAt: (wx: number, wy: number) => this.pickAt(wx, wy),
      });
    }
  }

  /**
   * Dev-only snapshot of the local world state for e2e assertions. `myPresence`/`activity` are read
   * off our own authoritative player schema entry; `nearestObjectId` is derived from the predicted
   * self position (what the in-world "Press E" prompt keys off). `players` is an extra (not part of
   * the required shape) so a multi-client e2e can read OTHER ducks' synced presence (the badge itself
   * is canvas-drawn and so invisible to DOM queries).
   */
  private debugSnapshot(): {
    mySessionId: string;
    myPresence: PresenceFlavor;
    activity: string;
    nearestObjectId: string | null;
    /** Predicted self world position — lets the e2e steer deterministically toward a target. */
    x: number;
    y: number;
    players: Record<
      string,
      {
        presence: string;
        activity: string;
        species: string;
        duckColor: string;
        cosmetics: DebugCosmetics;
        /** ADR-0039: the zone id this duck stands in right now (null = open commons), from its authoritative pos. */
        zoneId: string | null;
        /** ADR-0039: the rendered Pixi alpha for this duck FROM THE LOCAL VIEWER (1 same-room, 0.5 neighbour). */
        alpha: number;
        /** ADR-0039: is this duck currently drawn for the local viewer (same-room or open-neighbour + on-screen)? */
        visible: boolean;
      }
    >;
    /**
     * The local player's EQUIPPED cosmetic look (M6). Empty slots are omitted so the e2e can assert the
     * broadcast look exactly.
     */
    cosmetics: DebugCosmetics;
    /** The local player's coin balance (M6), pushed from the gateway-attested wallet, or null if unknown. */
    coins: number | null;
    /** Shared "Living World" ambient (M4.2): the same sky every duck sees, for e2e assertions. */
    timeOfDay: number;
    weather: string;
    dayPhase: DayPhase;
    /** Shared random world event (M7b): the same spontaneous moment every duck sees, for e2e assertions. */
    worldEvent: WorldEventKind;
    /** Rooms whose jukebox disco is ON right now (ADR-0031), sorted — for e2e to assert it's room-local. */
    discoZones: string[];
    /**
     * The guild's WORLD THEME currently skinning the world (M8 "Guilds at Scale") — the SAME engine,
     * re-skinned per guild. Read off the authoritative room state so a multi-client e2e can assert every
     * duck shares one skin (and that an owner's switch re-skins everyone).
     */
    theme: WorldThemeId;
    /** The themed world's human NAME (M8), for an e2e to assert the skinned world without a palette lookup. */
    themeName: string;
    /** Entryway front door (ADR-0022): is it open right now, and what front-door action is in range (or null). */
    entryDoorOpen: boolean;
    entryActionKind: EntryActionKind | null;
  } {
    const self = this.client.self();
    const near = this.nearestObjectInLevel(this.predicted.x, this.predicted.y);
    const players: Record<
      string,
      {
        presence: string;
        activity: string;
        species: string;
        duckColor: string;
        cosmetics: DebugCosmetics;
        zoneId: string | null;
        alpha: number;
        visible: boolean;
      }
    > = {};
    for (const [sid, p] of this.client.players) {
      // ADR-0039: surface the rendered alpha/visible STRAIGHT off the live sprite (the render loop sets
      // them per frame), so the e2e can assert the 0.5 neighbour fade from the viewer's actual screen
      // state — not just re-derive the predicate. Self reads alpha 1; an undrawn/culled duck reads
      // visible:false. Dev-only (this whole snapshot is dev-gated), so zero prod cost.
      const sprite = this.ducks.get(sid);
      players[sid] = {
        presence: p.presence ?? 'none',
        activity: p.activity,
        // ADR-0024: each player's synced ANIMAL + tint, so a multi-client e2e can verify the BROADCAST
        // species (the critter is canvas-drawn and so invisible to DOM queries).
        species: p.species ?? 'duck',
        duckColor: p.duckColor ?? 'yellow',
        // M6: each player's synced look so a multi-client e2e can verify the BROADCAST cosmetics (the
        // worn items are canvas-drawn and so invisible to DOM queries). Empty slots omitted.
        cosmetics: toDebugCosmetics(p.cosmetics),
        zoneId: this.zoneIdAt(p.x, p.y),
        alpha: sprite ? sprite.root.alpha : 1,
        visible: sprite ? sprite.root.visible : false,
      };
    }
    const sky = this.client.ambient();
    const themeId = this.client.theme();
    return {
      mySessionId: this.client.selfSessionId,
      myPresence: (self?.presence as PresenceFlavor) ?? 'none',
      activity: self?.activity ?? 'IDLE',
      nearestObjectId: near?.id ?? null,
      x: Math.round(this.predicted.x),
      y: Math.round(this.predicted.y),
      players,
      cosmetics: toDebugCosmetics(self?.cosmetics),
      coins: this.selfCoins,
      timeOfDay: sky.timeOfDay,
      weather: sky.weather,
      dayPhase: dayPhase(sky.timeOfDay),
      worldEvent: this.client.worldEvent(),
      discoZones: this.client.discoZoneIds().slice().sort(),
      theme: themeId,
      themeName: WORLD_THEMES[themeId].name,
      entryDoorOpen: this.client.entryDoorOpen(),
      entryActionKind: this.computeEntryAction(this.predicted.x, this.predicted.y)?.kind ?? null,
    };
  }

  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.camera.zoom = clamp(this.camera.zoom - Math.sign(e.deltaY) * 0.1, ZOOM_MIN, ZOOM_MAX);
  };

  private readonly loop = (ticker: Ticker): void => {
    if (!this.app || this.destroyed) return;
    const dtMs = Math.min(ticker.deltaMS, 50);
    const dt = dtMs / 1000;
    const now = performance.now();

    // ── Local prediction for our own duck (mirrors server integration). ──
    const v = this.input.vector();
    const len = Math.hypot(v.x, v.y);
    const nx = len > 0 ? v.x / len : 0;
    const ny = len > 0 ? v.y / len : 0;
    const moving = len > 0;
    // The PREVIOUS (assumed-valid) predicted position — the "prev" the wall solver slides off of.
    const prevX = this.predicted.x;
    const prevY = this.predicted.y;
    // Integrate + clamp to the world bounds first… (the RESOLVED level's size — the castle is 3600x2680,
    // not the office 1600x1200, so clamping to SIM would trap a castle duck near the centre).
    const targetX = clamp(
      prevX + nx * SIM.WALK_SPEED * dt,
      SIM.DUCK_RADIUS,
      this.level.worldWidth - SIM.DUCK_RADIUS,
    );
    const targetY = clamp(
      prevY + ny * SIM.WALK_SPEED * dt,
      SIM.DUCK_RADIUS,
      this.level.worldHeight - SIM.DUCK_RADIUS,
    );
    // …then resolve walls with the SAME pure function the server runs, against the SAME dynamic wall set —
    // `composeLevelWalls(entryDoorOpen, closedDoors)` folds in the entry-door slab while the front door is
    // shut (ADR-0022) AND a slab for every CLOSED interior door (Room Doors) — built from the RESOLVED
    // level's geometry so the local duck slides along walls / passes through open doors exactly like
    // authority → no wall rubber-banding, and a closed door blocks prediction too. Read once, reused below.
    const closedDoorIds = this.client.closedDoorIds();
    const entryDoorOpen = this.client.entryDoorOpen();
    const resolved = resolveWallCollisionIn(
      prevX,
      prevY,
      targetX,
      targetY,
      SIM.DUCK_RADIUS,
      this.composeLevelWalls(entryDoorOpen, closedDoorIds),
    );
    this.predicted.x = resolved.x;
    this.predicted.y = resolved.y;
    if (moving) this.predicted.facing = Math.atan2(ny, nx);

    // ── Throttled intent send (raw vector; the server clamps/normalizes). ──
    this.sendAccum += ticker.deltaMS;
    if (this.sendAccum >= SEND_INTERVAL_MS) {
      this.sendAccum %= SEND_INTERVAL_MS;
      // perf cost-census: don't pump a 20Hz zero-vector MOVE while standing still. Stream while moving,
      // plus ONE final zero on the moving→idle edge (so the server stops the duck promptly), then go quiet
      // until input resumes. Prediction/reconcile + `lastSeq` only matter while there are unacked predicted
      // moves (i.e. while moving), and the server's INTENT_DECAY zeroes stale input on its own — so an idle
      // duck needs no stream. A standing player drops ~20→0 MOVE msg/s (the PING keeps the socket alive).
      if (moving || this.lastSentMoving) {
        this.client.sendMove(++this.seq, v.x, v.y);
        this.lastSentMoving = moving;
      }
    }
    // Emotes are no longer keyboard-driven here — they're sent when the player picks one from the HUD's
    // Animations menu (client.sendEmote, via the onEmote prop). See features/office/useInteractionMenus.

    // ── Reconcile prediction against authority. ──
    const selfAuth = this.client.self();
    if (selfAuth) {
      const err = Math.hypot(this.predicted.x - selfAuth.x, this.predicted.y - selfAuth.y);
      if (err > SIM.RECONCILE_SNAP_THRESHOLD) {
        this.predicted.x = selfAuth.x;
        this.predicted.y = selfAuth.y;
      } else if (err > 8) {
        this.predicted.x += (selfAuth.x - this.predicted.x) * SIM.RECONCILE_EASE;
        this.predicted.y += (selfAuth.y - this.predicted.y) * SIM.RECONCILE_EASE;
      }
    }

    // ── Render every player. ──
    const renderTime = now - SIM.INTERPOLATION_DELAY_MS;
    // The room the local player is in (null = open commons). Used to OCCLUDE ducks the viewer can't see
    // past walls: you only see who you can hear (mirrors the server's same-room audio seal). Computed
    // once per frame off the predicted self position (the same position prediction + camera key off).
    const selfZoneId = this.zoneIdAt(this.predicted.x, this.predicted.y);
    // Live door state (ADR-0039) for the neighbour-presence tier, built ONCE per frame from the same
    // closedDoorIds/entryDoorOpen already read above for collision. A duck in a directly-connected
    // neighbour room (one OPEN doorway away) gets a dimmed glimpse; a closed door re-seals it.
    const doorState: DoorOpenState = { closedDoorwayIds: new Set(closedDoorIds), entryDoorOpen };
    for (const [sid, p] of this.client.players) {
      const duck = this.ensureDuck(sid, p.species as DuckSpecies, p.duckColor as DuckColor, p.displayName, sid === this.client.selfSessionId);

      if (sid === this.client.selfSessionId) {
        // The local player is always live + visible (never occluded by their own room's walls). The
        // per-frame setters below are idempotent + cheap; setPose runs the animation engine.
        duck.setSpeaking(this.speakingSessionIds.has(sid));
        duck.setSpeakLevel(this.speakingLevels.get(sid) ?? 0);
        duck.setPresence((p.presence as PresenceFlavor) ?? 'none');
        duck.setResting(p.activity === 'AFK' || p.activity === 'SLEEPING');
        duck.setCosmetics(p.cosmetics ?? { hat: '', glasses: '', outfit: '', pet: '' });
        duck.setHeldItem(p.heldItem ?? '');
        // ADR-0052: HP bar (shown only while hurt) + the on-duck fire while standing in the flames.
        duck.setHp(p.hp ?? HEALTH.MAX_HP, HEALTH.MAX_HP);
        duck.setBurning(!!p.burning);
        // My OWN outgoing webcam (ADR-0030) — the gold-bordered self bubble, exactly what nearby ducks see.
        duck.setWebcamStream(this.webcamStreams.get(sid) ?? null, true);
        duck.setPose(this.predicted.x, this.predicted.y, this.predicted.facing, moving, p.animation, dtMs);
        duck.root.zIndex = this.predicted.y;
        duck.root.visible = true;
        duck.root.alpha = 1; // self is always full opacity (ADR-0039 neighbour dim applies to remotes only)
        continue;
      }

      // Remote duck. Always keep its interpolation buffer warm (cheap append) so that if it re-enters
      // view there's recent data to sample and no positional pop.
      const buffer = this.sampleRemote(sid, p, now);
      const pose = sampleBuffer(buffer, renderTime) ?? { x: p.x, y: p.y, facing: p.facing };
      // PERF CULL (ADR-0025) + NEIGHBOUR GLIMPSE (ADR-0039): a remote duck is worth ANIMATING if the
      // viewer can see it — either it shares the viewer's room (occlusion: mirrors the server's same-room
      // audio seal) OR it's in a directly-connected NEIGHBOUR room through an OPEN doorway (a dimmed 50%
      // glimpse into the room next door — the world breathing at its seams), AND it falls inside the camera
      // viewport. Hidden ducks set visible=false (Pixi skips drawing + traversing the subtree) AND we skip
      // setPose + the per-frame setters entirely (the transform cost that dominates a crowded room). They
      // resume on their first visible frame (visible + setPose set together below, so no pop). A neighbour
      // duck through a CLOSED door / ≥2 hops away / in the commons stays fully sealed (hidden), exactly as
      // before. The visible count stays bounded by adjacency (one hop) + the camera cull.
      const peerZoneId = this.zoneIdAt(pose.x, pose.y);
      const sameRoom = peerZoneId === selfZoneId;
      const neighbor = !sameRoom && zonesConnectedOpenIn(this.level, selfZoneId, peerZoneId, doorState);
      const visible = (sameRoom || neighbor) && this.isOnScreen(pose.x, pose.y);
      duck.root.visible = visible;
      if (!visible) {
        // Keep the root POSITION current (two cheap assignments) so anything reading a remote duck's
        // world pos (e.g. celebration anchors) stays correct, but skip the expensive per-frame animation
        // + cosmetic work for a duck the viewer can't see. It resumes full animation on its first
        // visible frame below (position is already right, so there's no pop).
        duck.root.x = pose.x;
        duck.root.y = pose.y;
        // Stop decoding their webcam while culled (ADR-0025 + 0030): a hidden face costs nothing. The
        // stream stays subscribed (cheap); the loop resumes decode on its first visible frame below.
        duck.pauseWebcam();
        continue;
      }

      // ADR-0039: a same-room peer is full opacity; a neighbour-room peer (one OPEN doorway away) is the
      // dimmed 50% glimpse. Applied on the VISIBLE path only (hidden ducks short-circuited above).
      duck.root.alpha = sameRoom ? 1 : NEIGHBOR_PRESENCE_ALPHA;
      duck.setSpeaking(this.speakingSessionIds.has(sid));
      duck.setSpeakLevel(this.speakingLevels.get(sid) ?? 0);
      // M4: the citizen's ELECTED presence flair (a chosen badge, never inferred) and the gentle
      // resting cue (movement-derived AFK/SLEEPING — soft, never punitive). Both are idempotent.
      duck.setPresence((p.presence as PresenceFlavor) ?? 'none');
      duck.setResting(p.activity === 'AFK' || p.activity === 'SLEEPING');
      // M6: the citizen's EQUIPPED cosmetic look (self AND others), read straight off the synced schema.
      // Idempotent + cheap — rebuilds only a slot whose style changed. '' = empty slot.
      duck.setCosmetics(p.cosmetics ?? { hat: '', glasses: '', outfit: '', pet: '' });
      // Held item (Task 2) — the prop in the duck's hand; '' = empty. The field is server-synced.
      duck.setHeldItem(p.heldItem ?? '');
      // ADR-0052: HP bar (shown only while hurt) + the on-duck fire while standing in the flames.
      duck.setHp(p.hp ?? HEALTH.MAX_HP, HEALTH.MAX_HP);
      duck.setBurning(!!p.burning);
      // Nearby peer's webcam face bubble (ADR-0030) — only ever present for a duck we're allowed to see.
      duck.setWebcamStream(this.webcamStreams.get(sid) ?? null, false);
      duck.setPose(pose.x, pose.y, pose.facing, p.activity === 'WALKING', p.animation, dtMs);
      duck.root.zIndex = pose.y;
    }
    this.pruneDucks();

    // ── Living World ambient: drive the shared sky/weather from authoritative room state (M4.2). ──
    // Cheap + alloc-free: reads the in-place-mutated room scalars and lerps tint/positions in the layer.
    const sky = this.client.ambient();
    // TEMP: weather effects + day-night cycle disabled — pin to clear midday.
    // Restore with: this.ambient.update(sky.timeOfDay, sky.weather as Weather, dtMs);
    void sky;
    this.ambient.update(0.5, 'clear' as Weather, dtMs, this.cullTest);

    // ── Shared world event (M7b): build/teardown on a real change, then animate it each frame. ──
    // Read off the SAME authoritative room state as the sky; setEvent is idempotent + only rebuilds the
    // procedural decor when the kind actually changes (so it's free while an event runs / nothing's on).
    const event = this.client.worldEvent();
    if (event !== this.worldEventKind) {
      this.worldEventKind = event;
      this.worldEvent.setEvent(event);
    }
    this.worldEvent.update(dtMs);

    // ── Per-room disco (ADR-0031): reconcile the lit rooms to the authoritative `discoZones` set, then
    // animate each lit room's wash. Read off the SAME room state as the event; we build a cheap sorted
    // signature so we only reconcile (build/teardown tiles) when the set actually changes — free while a
    // disco steadily runs or nothing's lit. The jukebox toggles the set; the floor lights only its room.
    const discoIds = this.client.discoZoneIds();
    const discoKey = discoIds.slice().sort().join(',');
    if (discoKey !== this.discoZonesKey) {
      this.discoZonesKey = discoKey;
      this.disco.setActiveZones(discoIds);
    }
    this.disco.update(dtMs);

    // ── Placed furniture (Furniture Store): reconcile the rendered pieces to the authoritative `furniture`
    // set. Furniture is immutable once placed, so the SORTED ID SET is a sufficient signature — we only
    // rebuild when a piece is added (bought) or removed, free while the room's furniture is steady. ──
    if (this.currentArt) {
      const pieces = this.client.furniture();
      // Cheap ALLOCATION-FREE change signature: a numeric fold over count + each piece's transform (every
      // room now holds ~164 unbaked default pieces, so this runs every frame — a string map/sort/join here
      // would be steady-state GC churn). A move/rotate/add/remove changes the fold (or the count), so we
      // reconcile only on a real change; default furniture never moves, so the steady state is a no-op.
      let fold = pieces.length * 2654435761;
      for (const p of pieces) {
        fold = (fold * 31 + (p.x | 0) + (p.y | 0) * 7 + Math.round((p.rotation ?? 0) * 100) * 13) | 0;
      }
      const sig = `${this.themeId}:${pieces.length}:${fold}`;
      if (sig !== this.furnitureSig) {
        this.furnitureSig = sig;
        this.furnitureLayer.reconcile(pieces, this.currentArt);
      }
    }

    // ── World THEME (M8): re-skin the same world if the guild's theme changed (owner switched it). ──
    // Read off the SAME authoritative room state as the sky/event; re-skinning is idempotent + only
    // rebuilds the map when the theme actually changes (free while it's steady), so an owner's switch
    // re-skins the world live for everyone present — proving the engine, not the office, is the product.
    const theme = this.client.theme();
    if (theme !== this.themeId) this.applyTheme(theme);

    // ── Resolved GEOMETRY level (Phase 3): rebuild the world if the authoritative `levelId` changed. Like
    // the theme swap above, this is idempotent + only rebuilds on a real change (free while it's steady). ──
    if (this.client.levelId() !== this.levelId) this.resolveLevel();

    // ── Track the nearest interactable object to the predicted self pos (drives the HUD prompt). ──
    // The bare OFFICE's static objects aren't baked (every prop is editable furniture), so don't surface a
    // phantom "Press E" over them; the CASTLE bakes its objects, so it does. Per-level via bareLevelFor.
    const near = bareLevelFor(this.levelId) ? null : this.nearestObjectInLevel(this.predicted.x, this.predicted.y);
    if ((near?.id ?? null) !== this.nearestObjectId) {
      this.nearestObjectId = near?.id ?? null;
      this.onNearestObjectChange?.(near);
    }

    // ── Entryway front door (ADR-0022): swing the leaf toward its synced open/closed state, and track
    // the nearest front-door action (ring / knock / open) so the HUD can show the "Press E" prompt. ──
    this.updateEntryDoor(dtMs);
    const entry = this.computeEntryAction(this.predicted.x, this.predicted.y);
    if ((entry?.kind ?? null) !== (this.nearestEntryAction?.kind ?? null)) {
      this.nearestEntryAction = entry;
      this.onNearestEntryActionChange?.(entry);
    }

    // ── Interior room doors (Room Doors): swing each leaf toward its synced open/closed state, and track
    // the nearest doorway the local duck can operate so the HUD can show the "[E] Open/Close door" prompt. ──
    this.updateInteriorDoors(dtMs, closedDoorIds);
    // Read the HOLDS map (ADR-0060) at most ONCE per frame and only while something is closed — it is
    // shared by the prompt below and the doors signature, so a steadily-closed door costs one small
    // Map per frame, not several (weak-HW perf canon).
    const holds = closedDoorIds.length > 0 ? this.client.doorHolds() : null;
    const door = this.computeDoorAction(this.predicted.x, this.predicted.y, closedDoorIds, holds);
    if (
      (door?.doorwayId ?? null) !== (this.nearestDoorAction?.doorwayId ?? null) ||
      (door?.open ?? null) !== (this.nearestDoorAction?.open ?? null) ||
      (door?.locked ?? null) !== (this.nearestDoorAction?.locked ?? null)
    ) {
      this.nearestDoorAction = door;
      this.onNearestDoorActionChange?.(door);
    }
    // Surface the whole door state to the React layer (host panel) only when it actually changes. The
    // signature folds in each closed door's HOLDER (ADR-0060) so a hold decaying to soft ('') re-renders
    // the panel's 🔒 badges.
    const heldDoorIds = holds
      ? [...holds.entries()].filter(([, by]) => by !== '').map(([id]) => id).sort()
      : [];
    const doorsSig = `${entryDoorOpen ? 1 : 0}|${[...closedDoorIds].sort().join(',')}|${heldDoorIds.join(',')}`;
    if (doorsSig !== this.lastDoorsSig) {
      this.lastDoorsSig = doorsSig;
      this.onDoorsChange?.(closedDoorIds, entryDoorOpen, heldDoorIds);
    }

    // ── Light up the world object / seat I can use right now (the thing "E" acts on). Suppressed while
    //    seated — E just stands me up then, so there's no world target to point at. Cheap: nearestObject
    //    is already computed above; nearestSeat scans the small static seat catalog + the live roster for
    //    occupancy (mirrors the HUD's seat-prompt rule). The rings pulse via transform-only updates. ──
    const selfSeated = !!selfAuth?.seatedAt;
    if (!selfSeated && near) this.objectHighlight.showAt(near.x, near.y);
    else this.objectHighlight.hide();
    let seatToLight: Seat | null = null;
    if (!selfSeated && !bareLevelFor(this.levelId)) {
      const seat = this.nearestSeatInLevel(this.predicted.x, this.predicted.y);
      if (seat) {
        let taken = false;
        for (const [, p] of this.client.players) {
          if (p.seatedAt === seat.id) {
            taken = true;
            break;
          }
        }
        if (!taken) seatToLight = seat;
      }
    }
    if (seatToLight) this.seatHighlight.showAt(seatToLight.x, seatToLight.y);
    else this.seatHighlight.hide();
    this.objectHighlight.update(dtMs);
    this.seatHighlight.update(dtMs);

    // ── "Give reputation" gesture (press G near a teammate). ──
    // (The E "interact" key is handled at the window level → engine.interactNearest(), so the in-world
    // keypress and the accessible HUD button share one path.)
    if (this.input.consumeAction() === 'give-reputation') {
      const target = this.client.nearestOther(
        this.predicted.x,
        this.predicted.y,
        GIVE_REPUTATION_RANGE,
      );
      if (target) {
        this.onGiveReputation?.(target.player.userId, target.sessionId);
      }
    }

    // ── Spawn confetti for any celebrations received from the server. ──
    if (this.client.pendingCelebrations.length > 0) {
      for (const c of this.client.pendingCelebrations.splice(0)) {
        const pos = this.subjectPosition(c.sessionId);
        const burst = new CelebrationBurst(pos.x, pos.y, c.kind, c.text);
        this.fxLayer.addChild(burst.root);
        this.bursts.push(burst);
      }
    }

    // ── Spawn the piñata splatter for any duck that burst in the fire (ADR-0052). The death SPOT rides in
    //    the broadcast (the duck itself has already respawned at the spawn point, server-side). ──
    if (this.client.pendingBursts.length > 0) {
      for (const b of this.client.pendingBursts.splice(0)) {
        const burst = new DuckBurst(b.x, b.y, b.duckColor as DuckColor);
        this.fxLayer.addChild(burst.root);
        this.bursts.push(burst);
        // The death→spawn respawn is a single-tick teleport; drop this duck's interpolation buffer so
        // remote observers SNAP it to the spawn instead of lerping it streaking across the map (ADR-0052).
        this.buffers.delete(b.sessionId);
        this.lastSample.delete(b.sessionId);
      }
    }

    // ── M7a "Socialize": pop speech bubbles + float emoji reactions over the speaker's duck. ──
    // The bubble/emoji live ON the duck (so they bob + follow it), so we route to the existing Duck;
    // a broadcast for a duck not yet spawned this frame is harmlessly dropped (the speaker just left).
    if (this.client.pendingSaids.length > 0) {
      for (const s of this.client.pendingSaids.splice(0)) {
        this.ducks.get(s.sessionId)?.showSpeech(s.text);
      }
    }
    if (this.client.pendingReacts.length > 0) {
      for (const r of this.client.pendingReacts.splice(0)) {
        this.ducks.get(r.sessionId)?.showReaction(r.reaction);
      }
    }
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      if (this.bursts[i]!.update(dtMs)) this.bursts.splice(i, 1);
    }

    // ── Follow camera. ──
    // TODO(responsive): on tall/narrow viewports (e.g. 768x1024 portrait) a dark band can appear at the
    // top/bottom because the WORLD is 1600x1200 and the follow camera doesn't clamp to world bounds, so
    // near the spawn edge (y≈1080) the world runs out before the screen does. Investigated raising the
    // MIN/default zoom to a "fill" zoom (screenH/worldH ≈ 0.85 at 1024px tall) — but that (a) forces an
    // unwanted zoom-IN that changes the gameplay feel, (b) only helps at screen CENTRE since the camera
    // is unclamped (the band returns at the edges), and (c) would need the cull math (isOnScreen) revisited.
    // The correct fix is a bounds-CLAMPED camera (keep the world filling the viewport), which is a behaviour
    // change beyond this layout pass. Left as-is on purpose: correct gameplay > filling the band.
    this.camera.x += (this.predicted.x - this.camera.x) * 0.12;
    this.camera.y += (this.predicted.y - this.camera.y) * 0.12;
    const { width, height } = this.app.screen;
    this.world.scale.set(this.camera.zoom);
    this.world.x = width / 2 - this.camera.x * this.camera.zoom;
    this.world.y = height / 2 - this.camera.y * this.camera.zoom;

    // Keep the placement ghost under the cursor as the follow camera pans (the world transform just
    // moved). Cheap + only while a piece is armed; no-op otherwise.
    if (this.armed) this.syncGhost();
  };

  /**
   * Capture the current world as a PNG and trigger a download (M7a "Socialize" photo mode). Uses Pixi's
   * `renderer.extract` (NOT `canvas.toDataURL`, which is blank without `preserveDrawingBuffer`) to pull a
   * fresh frame off the stage, so it works regardless of the WebGL buffer-preservation setting. Returns
   * true if the download started, false if the renderer wasn't ready (the caller surfaces a gentle note).
   * The PNG is the only thing produced — nothing is uploaded, stored, or logged (a keepsake, not data).
   */
  async capturePhoto(): Promise<boolean> {
    if (!this.app) return false;
    try {
      // Extract a fresh frame of the whole stage (the framed, camera-positioned world) as a PNG data URL.
      const url = await this.app.renderer.extract.base64(this.app.stage);
      const a = document.createElement('a');
      a.href = url;
      a.download = `duck-office-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return true;
    } catch (e) {
      console.warn('[photo] capture failed', e);
      return false;
    }
  }

  /**
   * Re-skin the world to a new guild THEME (M8 "Guilds at Scale") — the SAME geometry/mechanics, a new
   * look. Rebuilds the themed map in place (swapping the bottom child of {@link world} so draw order is
   * preserved), re-tints the day/night sky wash + the canvas clear colour, and records the new theme.
   * Called only on a REAL change (from the loop), so it's free while the theme is steady. Never touches
   * ducks/cosmetics/zones/events/labels — purely the backdrop palette.
   */
  /**
   * Bake the live, static office-map container into a single backdrop Sprite (ADR-0026). Renders it once
   * to a texture we own (generateTexture — NOT the cacheAsTexture pool, which has a Pixi 8.5 destroy bug)
   * and returns a Sprite positioned to land exactly where the vector map sat. Sets {@link mapTexture};
   * the caller disposes the PREVIOUS texture after swapping. Baked at 2× device-independent resolution
   * so it's crisp at the default zoom (DPR-matched) — only mildly soft when zoomed fully in, and only on
   * the background; foreground ducks/door/labels stay live vector art. Falls back to the live container
   * if no renderer is available yet.
   */
  private bakeMap(live: Container): Container {
    if (!this.app) return live;

    const bounds = live.getLocalBounds();
    const texture = this.app.renderer.generateTexture({ target: live, resolution: 2, antialias: true });
    live.destroy({ children: true }); // baked into the texture — release the heavy live display tree
    this.mapTexture = texture;
    const sprite = new Sprite(texture);
    sprite.position.set(bounds.x, bounds.y);
    return sprite;
  }

  /**
   * cost-census: swap the office render resolution LIVE when the player changes their render-quality
   * preference (no reload). `renderer.resize(w, h, resolution)` re-bakes the drawing buffer at the new
   * pixel ratio while the CSS size + camera stay put — so the world just gets sharper or lighter. No-op
   * if the resolution is unchanged or the renderer is gone (destroyed / not yet inited).
   */
  applyRenderTier(tier: DeviceTier): void {
    const renderer = this.app?.renderer;
    if (!renderer) return;
    const resolution = officeRenderResolution(undefined, tier);
    if (renderer.resolution === resolution) return;
    renderer.resize(this.container.clientWidth, this.container.clientHeight, resolution);
  }

  private applyTheme(themeId: WorldThemeId): void {
    this.themeId = themeId;
    const theme = worldTheme(themeId);
    this.currentArt = themeArt(theme); // furniture/ghost re-skin to the new theme on next reconcile
    const prevTexture = this.mapTexture; // disposed after the swap so the live sprite never points at a freed texture
    // Re-skin against the SAME resolved geometry level (Phase 3) — only the cosmetic theme changed here.
    // bareLevel per level (office bare, castle baked) — mirrors the connect-time bake.
    const next = this.bakeMap(
      createOfficeMap(theme, layoutForLevel(this.level), this.level.worldWidth, this.level.worldHeight, {
        bareLevel: bareLevelFor(this.levelId),
        levelId: this.levelId,
      }),
    );
    // Swap the map at its original z-position (index 0 — the bottom of the world, below the washes/ducks)
    // so the re-skin can never reorder the scene graph. Destroy the old map's display tree (its children
    // are plain Graphics/Text we own) to release GPU resources — no shared/pooled nodes live here.
    if (this.map) {
      const idx = this.world.getChildIndex(this.map);
      this.world.removeChild(this.map);
      this.map.destroy({ children: true }); // destroys the old backdrop SPRITE (not its texture — default)
      this.world.addChildAt(next, Math.max(0, idx));
    } else {
      this.world.addChildAt(next, 0);
    }
    this.map = next;
    if (prevTexture && prevTexture !== this.mapTexture) prevTexture.destroy(true); // free the old baked texture
    this.ambient.setSkyTint(theme.palette.sky); // the next frame eases the wash toward it (no pop)
    if (this.app) this.app.renderer.background.color = this.themeBackground(theme.palette.sky);
  }

  /**
   * Resolve the authoritative GEOMETRY level from the synced `levelId` (Phase 3) and rebuild the world
   * against it if it changed. Mirrors {@link applyTheme}: rebuilds the baked map (now from the level's
   * layout + world size), re-binds the entry predicates + the interior-door leaves to the new geometry,
   * and reseeds the camera. Called once on init (when state is available) and again from the loop on a real
   * `levelId` change (defensive — the server resolves the level on join, so a live swap is rare). No-op when
   * the level is unchanged. The cosmetic theme is handled separately by {@link applyTheme}.
   */
  private resolveLevel(): void {
    const id = this.client.levelId();
    if (id === this.levelId && this.level === (LEVELS[id] ?? this.level)) return;
    this.levelId = id;
    this.level = LEVELS[id] ?? levelForTheme(this.client.theme());
    this.entry = entryPredicates(this.level.zones, this.level.entry);
    // Rebuild the baked backdrop against the new geometry (same swap-at-index-0 dance as applyTheme).
    const theme = worldTheme(this.themeId);
    const prevTexture = this.mapTexture;
    const next = this.bakeMap(
      createOfficeMap(theme, layoutForLevel(this.level), this.level.worldWidth, this.level.worldHeight, {
        bareLevel: bareLevelFor(this.levelId),
        levelId: this.levelId,
      }),
    );
    if (this.map) {
      const idx = this.world.getChildIndex(this.map);
      this.world.removeChild(this.map);
      this.map.destroy({ children: true });
      this.world.addChildAt(next, Math.max(0, idx));
    } else {
      this.world.addChildAt(next, 0);
    }
    this.map = next;
    if (prevTexture && prevTexture !== this.mapTexture) prevTexture.destroy(true);
    // Re-bind the entry door leaf + interior-door leaves to the new doorways/slabs.
    this.rebuildDoors();
    // Rebuild the ambient layer for the new level's objects + world size, in place at its z-position.
    const ambIdx = this.world.getChildIndex(this.ambient.root);
    this.world.removeChild(this.ambient.root);
    this.ambient.destroy();
    this.ambient = new AmbientLayer(bareLevelFor(this.levelId) ? [] : this.level.objects, this.level.worldWidth, this.level.worldHeight, this.level.fireHazards);
    this.ambient.setSkyTint(worldTheme(this.themeId).palette.sky);
    this.world.addChildAt(this.ambient.root, Math.max(0, ambIdx));
    // Rebuild the shared-event layer for the new level's rooms, in place at its z-position.
    const evtIdx = this.world.getChildIndex(this.worldEvent.root);
    this.world.removeChild(this.worldEvent.root);
    this.worldEvent.destroy();
    this.worldEvent = new WorldEventLayer(this.level.zones, this.level.worldWidth, this.level.worldHeight);
    this.worldEventKind = 'none'; // force a rebuild of any active event into the new layer next frame
    this.world.addChildAt(this.worldEvent.root, Math.max(0, evtIdx));
    // Re-point the disco wash at the new level's zones + force a reconcile next frame (drop stale washes).
    this.disco.setZones(this.level.zones);
    this.discoZonesKey = '';
  }

  /** Rebuild the entry-door leaf + every interior-door leaf for {@link level}'s geometry (on a level swap). */
  private rebuildDoors(): void {
    if (this.doorPanel) {
      this.world.removeChild(this.doorPanel);
      this.doorPanel.destroy({ children: true });
    }
    this.doorPanel = this.buildDoorPanel();
    // The leaf band sits below the ambient wash; re-insert it just above the map (index follows the map's).
    const mapIdx = this.map ? this.world.getChildIndex(this.map) : 0;
    this.world.addChildAt(this.doorPanel, Math.min(this.world.children.length, mapIdx + 1));
    this.buildInteriorDoors();
  }

  /** Nearest interactable OBJECT to a point within this level's interact range, or null (level-bound). */
  private nearestObjectInLevel(x: number, y: number): InteractiveObject | null {
    let best: InteractiveObject | null = null;
    let bestSq = OBJECT_INTERACT_RANGE * OBJECT_INTERACT_RANGE;
    for (const o of this.level.objects) {
      const dx = o.x - x;
      const dy = o.y - y;
      const sq = dx * dx + dy * dy;
      if (sq <= bestSq) {
        bestSq = sq;
        best = o;
      }
    }
    return best;
  }

  /** Nearest SEAT to a point within this level's seat-interact range, or null (level-bound). */
  private nearestSeatInLevel(x: number, y: number): Seat | null {
    let best: Seat | null = null;
    let bestSq = SEAT_INTERACT_RANGE * SEAT_INTERACT_RANGE;
    for (const s of this.level.seats) {
      const dx = s.x - x;
      const dy = s.y - y;
      const sq = dx * dx + dy * dy;
      if (sq <= bestSq) {
        bestSq = sq;
        best = s;
      }
    }
    return best;
  }

  /** Nearest controllable interior DOORWAY to a point within reach, or null (level-bound). */
  private nearestDoorwayInLevel(x: number, y: number): Doorway | null {
    let best: Doorway | null = null;
    let bestSq = DOOR_REACH * DOOR_REACH;
    for (const dw of this.level.doorways) {
      const dx = dw.threshold.x - x;
      const dy = dw.threshold.y - y;
      const sq = dx * dx + dy * dy;
      if (sq <= bestSq) {
        bestSq = sq;
        best = dw;
      }
    }
    return best;
  }

  /** Zone id a point sits in for THIS level (null = open commons), level-bound (mirrors `zoneAt`). */
  private zoneIdAt(x: number, y: number): string | null {
    return zoneAtIn(x, y, this.level.zones)?.id ?? null;
  }

  /**
   * The ACTIVE collidable wall set for THIS level right now: the static walls when the entry door is OPEN,
   * else the closed-entry set ({@link Level.wallsDoorClosed}, which folds in the entry-door slab) PLUS a
   * slab for every CLOSED interior doorway. Mirrors the office-default `composeWalls` but reads the resolved
   * level's geometry, so client prediction collides against the SAME world the server runs (Phase 2).
   */
  private composeLevelWalls(entryDoorOpen: boolean, closedDoorIds: string[]): readonly WorldWall[] {
    const base = entryDoorOpen ? this.level.walls : this.level.wallsDoorClosed;
    if (closedDoorIds.length === 0) return base;
    const closed = new Set(closedDoorIds);
    const slabs = this.level.doorways.filter((dw) => closed.has(dw.id)).map((dw) => dw.slab);
    return slabs.length === 0 ? base : [...base, ...slabs];
  }

  /**
   * The canvas clear-colour for a theme: a deeply-darkened variant of the theme SKY, so the area around
   * the world (letterboxing at wide aspect ratios) reads as the same place as the sky without competing
   * with the lit world. Cheap bit-twiddle; computed only on a theme change.
   */
  private themeBackground(skyHex: number): number {
    const r = Math.round(((skyHex >> 16) & 0xff) * 0.22);
    const g = Math.round(((skyHex >> 8) & 0xff) * 0.22);
    const b = Math.round((skyHex & 0xff) * 0.22);
    return (r << 16) | (g << 8) | b;
  }

  /**
   * Interact with the object nearest the local duck right now, if any is in range (M4). Used by the
   * HUD's accessible "Press E" affordance so interaction never depends solely on the in-world keypress
   * (a11y). Returns the object used, or null if nothing was in range.
   */
  interactNearest(): InteractiveObject | null {
    if (bareLevelFor(this.levelId)) return null; // bare office: no static objects to interact with via E (furniture only)
    const near = this.nearestObjectInLevel(this.predicted.x, this.predicted.y);
    if (near) this.onInteract?.(near.id);
    return near;
  }

  // ── Furniture Store placement (Buy & Place) + Take/move + context menu ──────

  /** The last pointer position over the canvas, in screen (CSS) coords — recomputed to world each frame so
   * the placement ghost tracks the cursor even as the follow camera pans. */
  private pointerScreen: { x: number; y: number } | null = null;
  /** Hit-test radius (world px) for picking a furniture piece / object under a right-click / long-press. */
  private static readonly PICK_RADIUS = 70;

  /** True while a furniture piece is armed for placement/move (the HUD shows the "click to place" cue). */
  isFurnitureArmed(): boolean {
    return this.armed !== null;
  }

  /**
   * ARM a freshly-bought catalog item for placement (Store "Buy & Place"): a translucent ghost follows the
   * cursor and snaps to the grid; clicking confirms (→ {@link onPlaceFurniture} → server BUY+place), Esc /
   * right-click cancels (nothing charged). The ghost is the SAME catalog art that gets placed (ТЗ §3).
   */
  armFurniture(item: FurnitureItem): void {
    if (!this.currentArt) return;
    this.beginArmed({ mode: 'buy', item }, buildFurnitureGhost(item, this.currentArt));
  }

  /**
   * "Take" a placed piece: pick it up and carry it (move mode). The piece is hidden in place while a ghost
   * of it follows the cursor; clicking drops it (→ server MOVE, no coins). Esc / right-click cancels and
   * the piece reappears where it was. Only meaningful for a piece the caller may modify (the React menu
   * gates this to the placer/host); the server re-checks regardless.
   */
  startMoveFurniture(furnitureId: string): void {
    if (!this.currentArt) return;
    const piece = this.client.furniture().find((p) => p.id === furnitureId);
    if (!piece) return;
    const ghost = buildGhostFromDecor(
      { id: 'ghost', kind: piece.kind, x: 0, y: 0, ...(piece.w ? { w: piece.w } : {}), ...(piece.h ? { h: piece.h } : {}), ...(piece.rotation ? { rotation: piece.rotation } : {}) },
      this.currentArt,
    );
    this.beginArmed(
      { mode: 'move', furnitureId, kind: piece.kind, w: piece.w, h: piece.h, rotation: piece.rotation ?? 0 },
      ghost,
    );
    this.furnitureLayer.setHidden(furnitureId); // it's "in your hands" now — hide the original
    this.mountCarried(piece); // …and show it held above the local duck's head while you walk
  }

  /** Mount a small solid copy of the carried piece above the LOCAL duck's head (ТЗ: walk around with it in
   * hand). Riding the duck's `root` means it follows movement automatically; cleared by {@link cancelFurniture}. */
  private mountCarried(piece: PlacedFurnitureView): void {
    if (!this.currentArt) return;
    const selfDuck = this.ducks.get(this.client.selfSessionId);
    if (!selfDuck) return; // no local sprite yet — the cursor ghost still conveys the carry
    const held = buildGhostFromDecor(
      { id: 'carried', kind: piece.kind, x: 0, y: 0, ...(piece.w ? { w: piece.w } : {}), ...(piece.h ? { h: piece.h } : {}) },
      this.currentArt,
    );
    held.alpha = 1; // solid (not a ghost) — it's really in hand
    held.scale.set(0.5); // hand-sized
    held.x = 0;
    held.y = -82; // clearly above the head + name label (head ≈ -14, label ≈ -34, badge ≈ -50)
    selfDuck.root.addChild(held);
    this.carriedOverlay = held;
  }

  /** Rotate a placed piece 90° in place (context-menu "Rotate") — a no-coin server move that keeps x/y. */
  rotateFurniture(furnitureId: string): void {
    const piece = this.client.furniture().find((p) => p.id === furnitureId);
    if (!piece) return;
    const rotation = (piece.rotation ?? 0) + Math.PI / 2;
    this.client.moveFurniture(furnitureId, piece.x, piece.y, rotation);
  }

  /** Shared arm setup for buy/move: mount the ghost, set the crosshair, attach the placement listeners. */
  private beginArmed(armed: NonNullable<OfficeEngine['armed']>, ghost: Container): void {
    if (!this.app) return;
    this.cancelFurniture(); // clear any previous arm (swap cleanly)
    this.armed = armed;
    this.furnitureGhost = ghost;
    this.world.addChild(ghost); // on top of the world so it's clearly the thing being placed/moved
    this.app.canvas.style.cursor = 'crosshair';
    this.app.canvas.addEventListener('pointermove', this.onPlacementPointerMove);
    this.app.canvas.addEventListener('pointerdown', this.onPlacementPointerDown);
    window.addEventListener('keydown', this.onPlacementKeyDown);
  }

  /** Cancel an armed placement/move (Esc / right-click / re-arm / teardown). Removes the ghost + listeners,
   * un-hides a carried piece, restores the cursor. Idempotent. NEVER charges coins — only a confirmed click. */
  cancelFurniture(): void {
    if (!this.armed && !this.furnitureGhost) return;
    if (this.armed?.mode === 'move') this.furnitureLayer.setHidden(null); // the carried piece reappears
    this.armed = null;
    if (this.furnitureGhost) {
      this.furnitureGhost.destroy({ children: true });
      this.furnitureGhost = null;
    }
    if (this.carriedOverlay) {
      this.carriedOverlay.destroy({ children: true }); // also detaches from the duck's root
      this.carriedOverlay = null;
    }
    this.pointerScreen = null;
    if (this.app) {
      this.app.canvas.style.cursor = 'default';
      this.app.canvas.removeEventListener('pointermove', this.onPlacementPointerMove);
      this.app.canvas.removeEventListener('pointerdown', this.onPlacementPointerDown);
    }
    window.removeEventListener('keydown', this.onPlacementKeyDown);
  }

  /** Convert a canvas screen (CSS) point to a WORLD coordinate using the live camera transform. */
  private screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const zoom = this.world.scale.x || 1;
    return { x: (sx - this.world.x) / zoom, y: (sy - this.world.y) / zoom };
  }

  /** The current snapped world position of the placement ghost (from the last pointer pos), or null. */
  private ghostWorldPos(): { x: number; y: number } | null {
    if (!this.pointerScreen) return null;
    const w = this.screenToWorld(this.pointerScreen.x, this.pointerScreen.y);
    return { x: snap(w.x, PLACEMENT_GRID), y: snap(w.y, PLACEMENT_GRID) };
  }

  private readonly onPlacementPointerMove = (e: PointerEvent): void => {
    if (!this.app) return;
    const rect = this.app.canvas.getBoundingClientRect();
    this.pointerScreen = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    this.syncGhost();
  };

  /** Position the ghost at the snapped cursor-world spot + tint it by whether the spot is inside a room. */
  private syncGhost(): void {
    if (!this.furnitureGhost) return;
    const pos = this.ghostWorldPos();
    if (!pos) return;
    this.furnitureGhost.x = pos.x;
    this.furnitureGhost.y = pos.y;
    setGhostValid(this.furnitureGhost, this.zoneIdAt(pos.x, pos.y) !== null);
  }

  private readonly onPlacementPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return; // left-click confirms; right-click cancels (see onCanvasContextMenu)
    const armed = this.armed;
    if (!armed) return;
    const rect = this.app?.canvas.getBoundingClientRect();
    if (rect) this.pointerScreen = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const pos = this.ghostWorldPos();
    if (!pos || this.zoneIdAt(pos.x, pos.y) === null) return; // ignore clicks outside a room (server would reject)
    e.preventDefault();
    e.stopPropagation();
    if (armed.mode === 'buy') {
      this.onPlaceFurniture?.(armed.item.id, pos.x, pos.y); // → server BUY + place
    } else {
      this.furnitureLayer.setHidden(null); // drop: the piece reappears (at its new spot once synced)
      this.client.moveFurniture(armed.furnitureId, pos.x, pos.y, armed.rotation); // → server MOVE (no coins)
    }
    this.cancelFurniture(); // one action per arm
  };

  private readonly onPlacementKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.cancelFurniture();
  };

  // ── Context menu (right-click / long-press over furniture or an interactive object) ──

  /** Find what's under a world point for the context menu: the nearest placed FURNITURE piece in reach, or
   * else the nearest interactive OBJECT in reach. Furniture wins ties (it sits on top). Null if nothing. */
  private pickAt(wx: number, wy: number): ContextMenuRequest['target'] | null {
    const r2 = OfficeEngine.PICK_RADIUS * OfficeEngine.PICK_RADIUS;
    let best: { piece: PlacedFurnitureView; d2: number } | null = null;
    for (const p of this.client.furniture()) {
      const d2 = (p.x - wx) ** 2 + (p.y - wy) ** 2;
      if (d2 <= r2 && (!best || d2 < best.d2)) best = { piece: p, d2 };
    }
    if (best) {
      const self = this.client.self();
      const canModify = best.piece.placedByUserId === self?.userId || this.isHost;
      const seatedHere = self?.seatedAt === best.piece.id;
      let occupied = false;
      if (isSittableFurniture(best.piece.kind) && !seatedHere) {
        for (const [sid, pl] of this.client.players) {
          if (sid !== this.client.selfSessionId && pl.seatedAt === best.piece.id) {
            occupied = true;
            break;
          }
        }
      }
      return {
        type: 'furniture',
        id: best.piece.id,
        kind: best.piece.kind,
        name: FURNITURE_NAME_BY_KIND.get(best.piece.kind) ?? 'Furniture',
        canModify,
        interactive: furnitureObjectKind(best.piece.kind) !== null,
        sittable: isSittableFurniture(best.piece.kind),
        seatedHere,
        occupied,
      };
    }
    const obj = bareLevelFor(this.levelId) ? null : this.nearestObjectInLevel(wx, wy);
    if (obj && (obj.x - wx) ** 2 + (obj.y - wy) ** 2 <= r2) {
      return { type: 'object', objectId: obj.id, label: obj.label, icon: obj.icon };
    }
    return null;
  }

  /** Raise a context-menu request for the React layer at the given screen point, or clear it if nothing's
   * under the cursor. While a placement/move is armed, the gesture cancels it instead (no menu). */
  private openContextMenuAt(clientX: number, clientY: number): void {
    if (!this.app) return;
    if (this.armed) {
      this.cancelFurniture();
      return;
    }
    const rect = this.app.canvas.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    const w = this.screenToWorld(sx, sy);
    const target = this.pickAt(w.x, w.y);
    this.onContextMenu?.(target ? { screenX: clientX, screenY: clientY, target } : null);
  }

  private readonly onCanvasContextMenu = (e: MouseEvent): void => {
    e.preventDefault(); // never the browser menu over the world
    this.openContextMenuAt(e.clientX, e.clientY);
  };

  // Touch long-press → the same context menu (no right mouse button on touch). A press that stays roughly
  // still for LONGPRESS_MS opens the menu; movement (a drag/pan) or an early lift cancels it.
  private static readonly LONGPRESS_MS = 480;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressStart: { x: number; y: number } | null = null;

  private readonly onCanvasPointerDownLong = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch' || this.armed) return;
    this.longPressStart = { x: e.clientX, y: e.clientY };
    this.clearLongPress();
    this.longPressTimer = setTimeout(() => {
      if (this.longPressStart) this.openContextMenuAt(this.longPressStart.x, this.longPressStart.y);
    }, OfficeEngine.LONGPRESS_MS);
  };
  private readonly onCanvasPointerMoveLong = (e: PointerEvent): void => {
    if (!this.longPressStart) return;
    if (Math.hypot(e.clientX - this.longPressStart.x, e.clientY - this.longPressStart.y) > 12) this.clearLongPress();
  };
  private readonly onCanvasPointerUpLong = (): void => this.clearLongPress();
  private clearLongPress(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  /**
   * The nearest ENTRYWAY front-door action the local duck can take right now (ADR-0022), or null. While
   * the door is OPEN there's nothing to do (just walk through). Otherwise: in reach of the door it's
   * "Open" (host, or already inside) or "Knock" (a waiting guest); in reach of the bell it's "Ring the
   * bell". If both the door and the bell are in reach, the CLOSER affordance wins. The SERVER re-checks
   * every gate — this only decides which prompt to surface. Pure (no side effects).
   */
  private computeEntryAction(px: number, py: number): EntryActionView | null {
    if (this.client.entryDoorOpen()) return null;

    let door: EntryActionView | null = null;
    if (this.entry.canOpenEntryDoor(px, py, this.isHost)) door = { kind: 'open', icon: '🚪', label: 'Open the door' };
    else if (this.entry.canKnockEntryDoor(px, py)) door = { kind: 'knock', icon: '✊', label: 'Knock' };

    const bell: EntryActionView | null = this.entry.canRingEntryBell(px, py)
      ? { kind: 'ring', icon: '🔔', label: 'Ring the bell' }
      : null;

    if (door && bell) {
      const dt = this.level.entry.doorThreshold;
      const b = this.level.entry.bell;
      const dDoor = (px - dt.x) ** 2 + (py - dt.y) ** 2;
      const dBell = (px - b.x) ** 2 + (py - b.y) ** 2;
      return dDoor <= dBell ? door : bell;
    }
    return door ?? bell;
  }

  /**
   * Perform the nearest entry action (ring / knock / open), if any is in range right now (ADR-0022).
   * Used by the HUD's accessible front-door button AND the in-world "E" key, so the gesture never depends
   * solely on the keypress (a11y). Returns the action taken, or null if nothing was in range.
   */
  triggerEntryAction(): EntryActionView | null {
    const action = this.computeEntryAction(this.predicted.x, this.predicted.y);
    if (action) this.onEntryAction?.(action.kind);
    return action;
  }

  /** True if the local duck has a front-door action in range right now (lets the HUD prioritise the E key). */
  hasEntryAction(): boolean {
    return this.computeEntryAction(this.predicted.x, this.predicted.y) !== null;
  }

  /**
   * Build the entry-door LEAF — the panel that fills the doorway when shut and swings open when the synced
   * state flips. Drawn in LOCAL coords hinged at its left edge (local x=0 = the world hinge), so scaling X
   * toward 0 reads as the door swinging inward. Positioned at the doorway slab ({@link ENTRY_DOOR_SLAB}).
   */
  private buildDoorPanel(): Container {
    const panel = new Container();
    const s = this.level.entry.doorSlab;
    panel.x = s.x; // hinge at the doorway's left jamb
    panel.y = s.y + s.height / 2; // vertical centre of the partition
    const half = s.height / 2 + 4; // a touch taller than the slab so it reads as a real door leaf
    const w = s.width;
    const leaf = new Graphics();
    leaf
      .roundRect(0, -half, w, half * 2, 4)
      .fill(0x9a6b3f) // warm wood
      .stroke({ width: 2, color: 0x5c3d22 });
    // Two recessed panels for a paneled-door look.
    leaf
      .roundRect(8, -half + 5, w / 2 - 12, half * 2 - 10, 2)
      .roundRect(w / 2 + 4, -half + 5, w / 2 - 12, half * 2 - 10, 2)
      .stroke({ width: 1.5, color: 0x744f2c });
    // A little brass knob near the leading (right) edge.
    leaf.circle(w - 12, 0, 3.2).fill(0xe7c66b).stroke({ width: 1, color: 0x6b5217 });
    panel.addChild(leaf);
    return panel;
  }

  /**
   * Ease the door leaf toward its synced open/closed state each frame (ADR-0022) — the leaf swings on its
   * hinge (scaleX → 0.12 open) and fades back, so the door never pops. Cheap + alloc-free; reads the
   * authoritative `entryDoorOpen` off room state (the same boolean collision prediction uses).
   */
  private updateEntryDoor(dtMs: number): void {
    if (!this.doorPanel) return;
    const target = this.client.entryDoorOpen() ? 1 : 0;
    // Time-based ease (~180ms to settle), framerate-independent.
    const k = Math.min(1, dtMs / 180);
    this.doorOpenAmount += (target - this.doorOpenAmount) * k;
    const a = this.doorOpenAmount;
    this.doorPanel.scale.x = 1 - a * 0.88; // swing toward the hinge
    this.doorPanel.alpha = 1 - a * 0.8; // and fade as it opens
    this.doorPanel.visible = a < 0.995;
  }

  /** Build one swinging leaf for every controllable interior doorway (Room Doors), all default OPEN. */
  private buildInteriorDoors(): void {
    this.interiorDoors.clear();
    this.doorLeafLayer.removeChildren();
    for (const dw of this.level.doorways) {
      const leaf = this.buildDoorLeaf(dw);
      this.doorLeafLayer.addChild(leaf.container);
      this.interiorDoors.set(dw.id, leaf);
    }
  }

  /**
   * Build a single interior-door leaf sized to its {@link Doorway.slab} and hinged at one jamb, so scaling
   * along the gap axis toward ~0 reads as the door swinging open. Horizontal doorways (N/S) hinge at the
   * LEFT jamb (scaleX); vertical doorways (E/W) hinge at the TOP jamb (scaleY). Styled like the entry leaf
   * (warm wood, two recessed panels, a brass knob). Starts hidden — doors default OPEN.
   */
  private buildDoorLeaf(dw: Doorway): DoorLeaf {
    const s = dw.slab;
    const container = new Container();
    const leaf = new Graphics();
    if (dw.axis === 'h') {
      container.x = s.x; // hinge at the doorway's left jamb
      container.y = s.y + s.height / 2; // vertical centre of the partition
      const half = s.height / 2 + 4;
      const w = s.width;
      leaf
        .roundRect(0, -half, w, half * 2, 4)
        .fill(0x9a6b3f)
        .stroke({ width: 2, color: 0x5c3d22 });
      leaf
        .roundRect(8, -half + 5, w / 2 - 12, half * 2 - 10, 2)
        .roundRect(w / 2 + 4, -half + 5, w / 2 - 12, half * 2 - 10, 2)
        .stroke({ width: 1.5, color: 0x744f2c });
      leaf.circle(w - 12, 0, 3.2).fill(0xe7c66b).stroke({ width: 1, color: 0x6b5217 });
    } else {
      container.x = s.x + s.width / 2; // horizontal centre of the partition
      container.y = s.y; // hinge at the doorway's top jamb
      const half = s.width / 2 + 4;
      const h = s.height;
      leaf
        .roundRect(-half, 0, half * 2, h, 4)
        .fill(0x9a6b3f)
        .stroke({ width: 2, color: 0x5c3d22 });
      leaf
        .roundRect(-half + 5, 8, half * 2 - 10, h / 2 - 12, 2)
        .roundRect(-half + 5, h / 2 + 4, half * 2 - 10, h / 2 - 12, 2)
        .stroke({ width: 1.5, color: 0x744f2c });
      leaf.circle(0, h - 12, 3.2).fill(0xe7c66b).stroke({ width: 1, color: 0x6b5217 });
    }
    container.addChild(leaf);
    container.visible = false; // default OPEN → hidden until shut
    return { container, axis: dw.axis, amount: 1 };
  }

  /**
   * Ease each interior-door leaf toward its synced open/closed state every frame (Room Doors) — a closed
   * door's leaf swings in to fill the gap, an open one swings out and fades. Cheap + alloc-light; reads the
   * authoritative `closedDoors` set (the same one collision prediction folds in via {@link composeWalls}).
   */
  private updateInteriorDoors(dtMs: number, closedDoorIds: string[]): void {
    const closed = closedDoorIds.length > 0 ? new Set(closedDoorIds) : null;
    const k = Math.min(1, dtMs / 180); // ~180ms ease, framerate-independent
    for (const [id, leaf] of this.interiorDoors) {
      const target = closed?.has(id) ? 0 : 1; // 0 = closed (leaf fills gap), 1 = open (out of the way)
      leaf.amount += (target - leaf.amount) * k;
      const a = leaf.amount;
      const swing = 1 - a * 0.88;
      if (leaf.axis === 'h') leaf.container.scale.x = swing;
      else leaf.container.scale.y = swing;
      leaf.container.alpha = 1 - a * 0.8;
      leaf.container.visible = a < 0.995;
    }
  }

  /**
   * The interior-door action the local duck can take right now (Room Doors), or null. The nearest doorway
   * within reach ({@link nearestDoorway}) becomes an "Open" (if it's currently shut) or "Close" prompt.
   * Anyone in reach may toggle (soft close); the HOST's "any door" power is the doors panel, not this
   * in-world prompt. Pure (no side effects). The SERVER re-checks {@link canToggleDoor} before acting.
   */
  private computeDoorAction(
    px: number,
    py: number,
    closedDoorIds: string[],
    holds?: ReadonlyMap<string, string> | null,
  ): DoorActionView | null {
    const dw = this.nearestDoorwayInLevel(px, py);
    if (!dw) return null;
    // canToggleDoor: the HOST may toggle any door; anyone else must be within DOOR_REACH of its threshold.
    // nearestDoorwayInLevel already gates on DOOR_REACH, so a non-host who reached it here qualifies.
    if (!this.isHost) {
      const d2 = (px - dw.threshold.x) ** 2 + (py - dw.threshold.y) ** 2;
      if (d2 > DOOR_REACH * DOOR_REACH) return null;
    }
    const isClosed = closedDoorIds.includes(dw.id);
    if (!isClosed) {
      return { doorwayId: dw.id, open: false, locked: false, icon: '🚪', label: 'Close door' };
    }
    // Mirror the server's `mayOpenHeldDoor` (ADR-0060) so the prompt is honest: a door someone else is
    // holding shut reads "Knock" (the send becomes a knock server-side), never a dead "Open door".
    // Judged off the PREDICTED position (like every prompt) while the server judges the authoritative
    // one — at a zone edge the label may flash for a frame, but the server stays the only authority.
    const heldBy = (holds ?? this.client.doorHolds()).get(dw.id) ?? '';
    const mayOpen = mayOpenHeldDoor({
      userId: this.client.self()?.userId ?? '',
      isHost: this.isHost,
      doorway: dw,
      heldBy,
      zoneId: this.zoneIdAt(px, py),
      doorways: this.level.doorways,
      closedDoorIds: new Set(closedDoorIds),
    });
    return mayOpen
      ? { doorwayId: dw.id, open: true, locked: false, icon: '🚪', label: 'Open door' }
      : { doorwayId: dw.id, open: true, locked: true, icon: '🔒', label: 'Knock (door is locked)' };
  }

  /**
   * Perform the nearest interior-door toggle, if one is in range (Room Doors). Used by the HUD's accessible
   * door button AND the in-world "E" key, so the gesture never depends solely on the keypress (a11y).
   * Returns the action taken, or null if no doorway was in range.
   */
  triggerDoorAction(): DoorActionView | null {
    const action = this.computeDoorAction(
      this.predicted.x,
      this.predicted.y,
      this.client.closedDoorIds(),
    );
    if (action) this.onDoorAction?.(action.doorwayId, action.open, action.locked);
    return action;
  }

  /** True if the local duck has an interior-door action in range right now (lets the HUD prioritise E). */
  hasDoorAction(): boolean {
    return this.nearestDoorwayInLevel(this.predicted.x, this.predicted.y) !== null;
  }

  /** The interior-door action currently in range (the last one tracked), or null — for e2e observability. */
  currentDoorAction(): DoorActionView | null {
    return this.nearestDoorAction;
  }

  /**
   * Challenge the nearest OTHER duck to a minigame (M7b-2 "Play Together" — `game` picks RPS or the
   * Bark Battle), if one is in range. Used by the HUD's accessible "Challenge" picker — it picks the
   * target off the predicted self position with the SAME proximity query give-reputation uses (the
   * realtime re-checks proximity authoritatively). Returns the targeted session id, or null if no
   * duck was in range.
   */
  challengeNearest(game: 'rps' | 'bark' = 'rps'): string | null {
    const target = this.client.nearestOther(this.predicted.x, this.predicted.y, MINIGAME_RANGE);
    if (target) this.onChallengeNearest?.(target.player.userId, target.sessionId, game);
    return target?.sessionId ?? null;
  }

  /**
   * Replace the set of ducks shown as "speaking" (ADR-0009 visible-speaking indicator). Driven by the
   * voice client from the server's authoritative ACTIVE_SPEAKERS set. Ducks not yet spawned pick up
   * the state on their next render tick, so this is safe to call any time.
   */
  setSpeaking(sessionIds: Iterable<string>): void {
    this.speakingSessionIds.clear();
    for (const sid of sessionIds) this.speakingSessionIds.add(sid);
    // Apply immediately to already-spawned ducks; any not-yet-spawned ones are handled in the loop.
    for (const [sid, duck] of this.ducks) duck.setSpeaking(this.speakingSessionIds.has(sid));
  }

  /**
   * Attach or clear a player's webcam stream (ADR-0030), pushed from the voice client for SELF (your own
   * outgoing camera) and for each nearby peer whose face the server forwards to you. The render loop
   * applies it to that duck's head bubble each frame (idempotent); a null stream clears the bubble. We
   * apply it eagerly to an already-visible duck so a pin/toggle reflects without waiting a frame.
   */
  setPeerWebcam(sessionId: string, stream: MediaStream | null): void {
    if (stream) this.webcamStreams.set(sessionId, stream);
    else this.webcamStreams.delete(sessionId);
    const duck = this.ducks.get(sessionId);
    if (duck && duck.root.visible) {
      duck.setWebcamStream(stream, sessionId === this.client.selfSessionId);
    }
  }

  /** Dev/e2e (ADR-0030): the webcam-bubble render state of a given player, or null if no critter spawned. */
  webcamRenderState(sessionId: string): ReturnType<Critter['webcamRenderState']> | null {
    return this.ducks.get(sessionId)?.webcamRenderState() ?? null;
  }

  /**
   * Update the live LOUDNESS (0..1) of each speaking voice (from the voice client's meter), driving how
   * much each duck SWELLS while it talks — a louder shout is physically bigger on screen. Sessions
   * absent from the map are silent (level 0). Stored for the render loop; applied to spawned ducks here
   * so an off-screen→on-screen duck never pops to a stale size. Cheap (a Map ref swap + a short walk).
   */
  setSpeakingLevels(levels: Map<string, number>): void {
    this.speakingLevels = levels;
    for (const [sid, duck] of this.ducks) duck.setSpeakLevel(levels.get(sid) ?? 0);
  }

  private sampleRemote(
    sid: string,
    p: { x: number; y: number; facing: number },
    now: number,
  ): Snapshot[] {
    let buffer = this.buffers.get(sid);
    if (!buffer) {
      buffer = [];
      this.buffers.set(sid, buffer);
    }
    const last = this.lastSample.get(sid);
    if (!last || last.x !== p.x || last.y !== p.y) {
      buffer.push({ t: now, x: p.x, y: p.y, facing: p.facing });
      this.lastSample.set(sid, { x: p.x, y: p.y });
    }
    trimBuffer(buffer, now, 1000);
    return buffer;
  }

  /** World position to anchor a celebration on: the predicted self pos, or a remote duck's pos. */
  private subjectPosition(sessionId: string): { x: number; y: number } {
    if (sessionId === this.client.selfSessionId) {
      return { x: this.predicted.x, y: this.predicted.y };
    }
    const duck = this.ducks.get(sessionId);
    if (duck) return { x: duck.root.x, y: duck.root.y };
    const p = this.client.players.get(sessionId);
    return { x: p?.x ?? this.level.worldWidth / 2, y: p?.y ?? this.level.worldHeight / 2 };
  }

  /**
   * Is a world point currently inside the camera viewport (plus a margin so avatars near the edge don't
   * pop)? Used by the per-frame cull (ADR-0025) to skip animating remote ducks the viewer can't see.
   * The world is scaled by camera.zoom and centred on camera.x/y (see the camera-follow block), so a
   * point is on-screen when it's within half the screen (in world units) of the camera centre.
   */
  private isOnScreen(wx: number, wy: number): boolean {
    if (!this.app) return true;
    const { width, height } = this.app.screen;
    const z = this.camera.zoom || 1;
    const MARGIN = 140; // world units of slack (an avatar + label is ~90px tall) so edges never pop
    const halfW = width / 2 / z + MARGIN;
    const halfH = height / 2 / z + MARGIN;
    return Math.abs(wx - this.camera.x) <= halfW && Math.abs(wy - this.camera.y) <= halfH;
  }

  private ensureDuck(sid: string, species: DuckSpecies, color: DuckColor, name: string, isSelf: boolean): Critter {
    let duck = this.ducks.get(sid);
    if (!duck) {
      duck = new Critter(species, color, name, isSelf);
      // Tapping this duck's webcam bubble pins their camera (ADR-0030). Wired once at spawn; `sid` is
      // stable for the critter's lifetime. Self taps are ignored downstream (you have the self-preview).
      duck.onWebcamTap = () => this.onWebcamClick?.(sid);
      this.ducks.set(sid, duck);
      this.actors.addChild(duck.root);
    }
    return duck;
  }

  private pruneDucks(): void {
    for (const [sid, duck] of this.ducks) {
      if (!this.client.players.has(sid)) {
        duck.destroy();
        this.ducks.delete(sid);
        this.buffers.delete(sid);
        this.lastSample.delete(sid);
      }
    }
  }

  destroy(): void {
    this.destroyed = true;
    if (typeof window !== 'undefined' && import.meta.env?.DEV) {
      delete (window as unknown as { __duckWorld?: () => unknown }).__duckWorld;
      delete (window as unknown as { __duckSocial?: () => unknown }).__duckSocial;
    }
    this.input.detach();
    this.cancelFurniture(); // remove any armed-placement ghost + its listeners before teardown
    this.clearLongPress();
    if (this.app) {
      this.app.canvas.removeEventListener('contextmenu', this.onCanvasContextMenu);
      this.app.canvas.removeEventListener('pointerdown', this.onCanvasPointerDownLong);
      this.app.canvas.removeEventListener('pointermove', this.onCanvasPointerMoveLong);
      this.app.canvas.removeEventListener('pointerup', this.onCanvasPointerUpLong);
      this.app.canvas.removeEventListener('pointercancel', this.onCanvasPointerUpLong);
    }
    // Tear down every critter explicitly first: their webcam video elements + textures (ADR-0030) are
    // plain DOM/GPU resources that app.destroy won't reach. Critter.destroy removes its root from the
    // parent, so the app.destroy below never double-frees it. Drop the stream refs (the voice client
    // owns + stops the underlying tracks).
    for (const duck of this.ducks.values()) duck.destroy();
    this.ducks.clear();
    this.webcamStreams.clear();
    if (this.app) {
      this.app.canvas.removeEventListener('wheel', this.onWheel);
      this.app.ticker.remove(this.loop);
      this.app.destroy(true, { children: true });
      this.app = null;
    }
    this.ambient.destroy();
    this.worldEvent.destroy();
    this.disco.destroy();
    this.furnitureLayer.destroy();
    // The map's display tree was torn down by app.destroy(..., {children:true}) above; just drop our
    // reference so a late call can't touch a destroyed node (M8). Never double-destroy.
    this.map = null;
    // The baked-map texture (ADR-0026) is owned by us, not pooled, and Sprite.destroy never frees its
    // texture — so release it explicitly here (after the sprite is gone) to avoid a GPU-texture leak.
    this.mapTexture?.destroy(true);
    this.mapTexture = null;
    this.doorPanel = null; // torn down with the world by app.destroy({children:true}); just drop the ref
    this.interiorDoors.clear(); // leaves are torn down with the world; just drop the refs
    this.ducks.clear();
    this.buffers.clear();
    this.lastSample.clear();
    this.bursts.length = 0;
  }
}
